import { useTranslation } from 'react-i18next';
import { useEffect, useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { staticQueryPolicy } from '../../api/queryPolicies';
import type {
  AdminUserResponse,
  AdminInterestSummaryResponse,
  AdminInterestDetailResponse,
} from '../../api/types';
import {
  adminPlaceBid,
  adminUpdateBid,
  adminConvertInterestToBid,
  getMarginConfig,
  getUserById,
  type BidResponse,
} from '../../api/endpoints/admin';
import { useMutationHook } from '../../hooks/useMutationHook';
import { UserPicker } from '../../components/domain/UserPicker';
import styles from '../../components/domain/FormModal.module.css';
import { DatePicker } from '../../components/ui/DatePicker';
import { DialogShell } from '../../components/ui/DialogShell';
import { useDialogDismiss, useIsDirty } from '../../components/ui/dialogDismiss';
import { BidPricingBlock } from './BidPricingBlock';
import { buildShipperPricing, initPricing, shipperAmountError, type PricingState } from './bidPricing';
import { DialogTitleRow } from '../../help/DialogTitleRow';

interface Props {
  shipmentId: number;
  /** When set, edits this bid (must be PENDING; bidder unchangeable) */
  bid?: BidResponse | null;
  /** Fleet manager to preselect as the bidder (create mode only) */
  initialBidderId?: number;
  /** When set, converts this interest into a bid */
  interest?: AdminInterestSummaryResponse | AdminInterestDetailResponse | null;
  onClose: () => void;
}

/** Add / Edit bid on behalf of a partner (fleet manager). */
export function BidFormModal({ shipmentId, bid, initialBidderId, interest, onClose }: Props) {
  const { t } = useTranslation();
  const isEdit = !!bid;
  const queryClient = useQueryClient();
  const [conversionConflict, setConversionConflict] = useState(false);
  const isConvert = !!interest;
  const [bidder, setBidder] = useState<AdminUserResponse | null>(null);
  const [prefillApplied, setPrefillApplied] = useState(false);

  const effectiveBidderId = initialBidderId ?? interest?.vendorId;

  const { data: initialBidderDetail } = useQuery({
    queryKey: ['admin', 'users', 'detail', effectiveBidderId],
    queryFn: () => getUserById(effectiveBidderId!),
    enabled: !isEdit && !isConvert && !!effectiveBidderId,
    ...staticQueryPolicy,
  });

  useEffect(() => {
    if (initialBidderDetail && !prefillApplied) {
      setBidder(initialBidderDetail.user);
      setPrefillApplied(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialBidderDetail]);
  // Pricing block (#221): edit opens with the bid's server values; create /
  // convert prefill the default margin once the config loads.
  const [pricing, setPricing] = useState<PricingState>(() =>
    initPricing({
      carrier: bid?.amount ?? null,
      shipper: bid?.shipperFacingAmount ?? null,
      pct: bid ? (bid.shipperMarginPct ?? bid.effectiveMarginPct ?? null) : null,
      fixed: bid?.shipperAmountFixed === true,
    }),
  );
  const price = pricing.carrier;
  const [etaDate, setEtaDate] = useState<string>(() => {
    if (bid?.proposedPickupTime) {
      const d = new Date(bid.proposedPickupTime);
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }
    if (bid?.etaHours) {
      const d = new Date(Date.now() + bid.etaHours * 3600 * 1000);
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }
    return '';
  });
  const [notes, setNotes] = useState(bid?.notes ?? interest?.vendorNote ?? '');
  const [error, setError] = useState<string | null>(null);

  // Loaded in edit mode too: "Reset to default %" needs the default.
  const { data: marginConfig } = useQuery({
    queryKey: ['admin', 'margin-config'],
    queryFn: getMarginConfig,
    ...staticQueryPolicy,
  });

  // Set together with the prefill so the dirty check re-snapshots on that render.
  const [marginPrefilled, setMarginPrefilled] = useState(false);
  useEffect(() => {
    if (!isEdit && marginConfig && pricing.pct === '') {
      // A prefill, not the user's edit: keep the touched flags and lastEdited as they were.
      setPricing((s) => ({
        ...s,
        pct: String(marginConfig.shipperMarginPct),
        shipper: s.fixed
          ? s.shipper
          : initPricing({
              carrier: s.carrier !== '' ? Number(s.carrier) : null,
              shipper: null,
              pct: marginConfig.shipperMarginPct,
              fixed: false,
            }).shipper,
      }));
      setMarginPrefilled(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marginConfig]);

  const saveMut = useMutationHook<BidResponse | AdminInterestDetailResponse, void>(
    () => {
      const computedEtaHours = etaDate
        ? Math.max(1, Math.round((new Date(etaDate).getTime() - Date.now()) / (1000 * 3600)))
        : 0;
      const proposedPickupIso = etaDate ? new Date(etaDate).toISOString() : undefined;
      // R10: shipperAmount XOR a margin %, never both.
      const shipperPricing = buildShipperPricing(isEdit ? 'edit' : 'create', pricing);
      if (isConvert) {
        return adminConvertInterestToBid(interest!.id, {
          expectedVersion: interest!.version,
          price: Number(price),
          etaHours: computedEtaHours,
          notes: notes.trim() || undefined,
          proposedPickupTime: proposedPickupIso,
          ...(shipperPricing && 'shipperAmount' in shipperPricing
            ? { shipperAmount: shipperPricing.shipperAmount }
            : shipperPricing
              ? { marginOverride: shipperPricing.shipperMarginPct }
              : {}),
        });
      }
      if (isEdit) {
        return adminUpdateBid(
          bid!.id,
          {
            price: Number(price),
            etaHours: computedEtaHours,
            notes: notes.trim() || undefined,
            proposedPickupTime: proposedPickupIso,
          },
          shipperPricing,
        );
      }
      return adminPlaceBid(
        shipmentId,
        bidder!.id,
        {
          price: Number(price),
          etaHours: computedEtaHours,
          notes: notes.trim() || undefined,
          proposedPickupTime: proposedPickupIso,
        },
        shipperPricing,
      );
    },
    {
      successMessage: isConvert
        ? 'Interest converted to bid successfully.'
        : isEdit
          ? 'Bid updated.'
          : 'Bid placed on behalf of the bidder.',
      invalidateKeys: [
        ['admin', 'interests'],
        ...(interest ? [['admin', 'interests', interest.id]] : []),
        ['admin', 'shipments', 'bids', shipmentId],
        ['admin', 'shipments', 'detail', shipmentId],
      ],
      onError: (error) => {
        if (isConvert && (error as { response?: { status?: number } }).response?.status === 409) {
          setConversionConflict(true);
          void queryClient.invalidateQueries({ queryKey: ['admin', 'interests'] });
          void queryClient.invalidateQueries({ queryKey: ['admin', 'shipments'] });
        }
      },
      onSuccess: onClose,
    },
  );

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (conversionConflict) return;
    if (!isEdit && !isConvert && !bidder) return setError('Please select the bidder.');
    if (!price || Number(price) <= 0) return setError('Partner price must be greater than zero.');
    const shipperError = shipperAmountError(pricing);
    if (shipperError) return setError(shipperError);
    if (!etaDate) return setError('Please select a pickup date and time.');
    const etaMs = new Date(etaDate).getTime() - Date.now();
    if (etaMs <= 0) return setError('Pickup date must be in the future.');
    if (!pricing.fixed && pricing.pct !== '') {
      const m = Number(pricing.pct);
      if (Number.isNaN(m)) return setError('Shipper margin must be a number.');
      if (Math.round(m * 100) !== m * 100)
        return setError('Shipper margin supports at most 2 decimal places.');
    }
    setError(null);
    saveMut.mutate(undefined);
  };

  const title = isConvert
    ? `Convert interest #${interest!.id} to bid`
    : isEdit
      ? `Edit bid #${bid!.id}`
      : 'Bid(Rate)';
  // Prefilled bidder / margin are not the user's edits: re-snapshot once they land.
  const dirty = useIsDirty(
    {
      bidderId: bidder?.id ?? null,
      price,
      shipper: pricing.shipper,
      pct: pricing.pct,
      fixed: pricing.fixed,
      etaDate,
      notes,
    },
    `${prefillApplied}-${marginPrefilled}`,
  );
  const dismiss = useDialogDismiss({ dirty, busy: saveMut.isLoading, onClose });

  return (
    <DialogShell dismiss={dismiss} label={title} scrimClassName={styles.scrim} panelClassName={styles.panel}>
      <DialogTitleRow context={isConvert ? 'interests' : 'bids-and-award'} title={title}>
        <h2 className={styles.title}>{title}</h2>
      </DialogTitleRow>

      {conversionConflict && (
        <div role="alert" className={styles.conflictBanner}>
          <span>
            {t('shipments.bidFormModal.conflict')}
          </span>
          <button type="button" className={styles.conflictBtn} onClick={onClose}>
            {t('shipments.bidFormModal.conflictReturn')}
          </button>
        </div>
      )}
      {isConvert && (
        <div
          style={{
            background: 'var(--status-green-bg)',
            border: '1px solid var(--status-green-border)',
            borderRadius: 'var(--radius-button)',
            padding: '10px 14px',
            marginBottom: 14,
          }}
        >
          <div
            style={{
              fontSize: 11,
              color: 'var(--status-green-text)',
              fontWeight: 700,
              textTransform: 'uppercase',
            }}
          >
            {t('shipments.bidFormModal.converting', { interestId: interest!.id })}
          </div>
          <div
            style={{
              fontSize: 13,
              color: 'var(--status-green-text)',
              fontWeight: 600,
              marginTop: 2,
            }}
          >
            {t('shipments.bidFormModal.vendor')}
            {interest!.vendorName || `Partner #${interest!.vendorId}`} (
            {interest!.vendorPhone || 'No phone'})
          </div>
          {interest!.vendorNote && (
            <div style={{ fontSize: 12, color: 'var(--status-green-text)', marginTop: 4 }}>
              <strong>{t('shipments.bidFormModal.vendorNote')}</strong> {interest!.vendorNote}
            </div>
          )}
        </div>
      )}

      <form onSubmit={submit}>
        {!isEdit && !isConvert && (
          <>
            <label className={styles.label}>{t('shipments.bidFormModal.bidder')}</label>
            <UserPicker role="FLEET_MANAGER" value={bidder} onChange={setBidder} />
          </>
        )}

        <BidPricingBlock
          value={pricing}
          onChange={setPricing}
          defaultPct={marginConfig?.shipperMarginPct ?? null}
        />

        <label className={styles.label} htmlFor="bid-pickup-datetime">{t('shipments.bidFormModal.pickupLabel')}</label>
        <DatePicker
          withTime
          id="bid-pickup-datetime"
          aria-label={t('shipments.bidFormModal.pickupLabel')}
          className={styles.input}
          value={etaDate}
          onChange={setEtaDate}
          min={new Date(Date.now() + 60 * 1000).toISOString().slice(0, 16)}
        />

        <label className={styles.label}>{t('shipments.bidFormModal.notesLabel')}</label>
        <input
          className={styles.input}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={t('shipments.bidFormModal.notesPlaceholder')}
        />

        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.actions}>
          <button type="button" className={styles.cancel} onClick={dismiss.requestClose}>
            {t('common.actions.cancel')}
          </button>
          <button type="submit" className={styles.submit} disabled={saveMut.isLoading || conversionConflict}>
            {saveMut.isLoading
              ? 'Saving…'
              : isConvert
                ? 'Confirm & Convert to Bid'
                : isEdit
                  ? 'Save changes'
                  : 'Place bid'}
          </button>
        </div>
      </form>
    </DialogShell>
  );
}
